import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import MicroSurvey from '../components/MicroSurvey';
import SurveyTimer from '../components/SurveyTimer';
import PushNotificationPrompt from '../components/PushNotificationPrompt';

export default function SurveyPage() {
  const { token } = useParams();
  const [survey, setSurvey] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(true);

  useEffect(() => {
    api.get(`/surveys/${token}`)
      .then((res) => {
        if (res.data.already_responded) {
          setError('Vous avez déjà répondu à ce sondage.');
        } else {
          setSurvey(res.data);
        }
      })
      .catch((err) => setError(err.response?.data?.message || 'Sondage non trouvé'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (submitted && token) {
      api.get(`/surveys/${token}/stats`)
        .then((res) => setStats(res.data))
        .catch(() => setStats({ total_participations: 0, response_rate: 0, message: '' }));
    }
  }, [submitted, token]);

  const handleSuccess = () => {
    setSubmitted(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (error && !survey) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Vérifiez le lien reçu ou contactez votre administrateur.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 overflow-x-hidden">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center animate-fade-in">
          <p className="text-2xl font-semibold text-gray-800 mb-4">Merci !</p>
          <p className="text-gray-600 mb-6">Vos réponses ont été enregistrées.</p>
          <p className="text-primary-600 font-medium mb-4">🔒 Vos réponses sont 100% anonymes et non traçables.</p>
          {stats && (
            <div className="text-sm text-gray-500 space-y-1">
              <p>{stats.total_participations} personne(s) ont participé</p>
              {stats.response_rate !== undefined && (
                <p>Taux de réponse : {stats.response_rate}%</p>
              )}
            </div>
          )}
          {showPushPrompt && (
            <PushNotificationPrompt onDismiss={() => setShowPushPrompt(false)} />
          )}
        </div>
      </div>
    );
  }

  return (
    <main id="main-content" className="min-h-screen bg-gray-50 p-4 overflow-x-hidden" tabIndex={-1}>
      <div className="max-w-lg mx-auto py-8 sm:py-12">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">{survey.title}</h1>
        {survey.description && (
          <p className="text-gray-600 mb-6">{survey.description}</p>
        )}
        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-2">~30 secondes</p>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <SurveyTimer duration={30} className="h-full bg-primary-500 rounded-full transition-all duration-1000 ease-linear" />
          </div>
        </div>
        <MicroSurvey survey={survey} token={token} onSuccess={handleSuccess} />
      </div>
    </main>
  );
}
